/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from "react";
import {
  IonContent,
  IonFab,
  IonFabButton,
  IonIcon,
  IonItem,
  IonLabel,
  IonPopover,
  IonRouterOutlet,
  IonTabBar,
  IonTabButton,
  IonTabs,
} from "@ionic/react";
import { Redirect, Route, useHistory, useLocation } from "react-router";

/* Core CSS required for Ionic components to work properly */
import "@ionic/react/css/core.css";

/* Basic CSS for apps built with Ionic */
import "@ionic/react/css/normalize.css";
import "@ionic/react/css/structure.css";
import "@ionic/react/css/typography.css";

/* Optional CSS utils that can be commented out */
import "@ionic/react/css/padding.css";
import "@ionic/react/css/float-elements.css";
import "@ionic/react/css/text-alignment.css";
import "@ionic/react/css/text-transformation.css";
import "@ionic/react/css/flex-utils.css";
import "@ionic/react/css/display.css";

import "../App.css";
import { add } from "ionicons/icons";

import { useRecoilValue, useRecoilValueLoadable, useSetRecoilState } from "recoil";
import { evidenceAtom, progressCheckDataAtom, userAtom, userProgramsAtom } from "@state/State";
// import EvidenceModal from "@components/common/EvidenceModalRefactored";
import EvidenceModal from "@components/common/EvidenceModal";
import {
  TabBar_Dash_Active,
  TabBar_Dash_Inactive,
  Tab_Evidence_Active,
  Tab_Evidence_Inactive,
  Tab_Guidelines_Active,
  Tab_Guidelines_Inactive,
  Tab_Profile_Active,
  Tab_Profile_Inactive,
  Tab_Timeline_Active,
  Tab_Timeline_Inactive,
} from "@assets/images";
import { EventRegister } from "react-native-event-listeners";
import * as FirebaseService from "@controllers/FirebaseService";
import { NavigationBar } from "@hugotomazi/capacitor-navigation-bar";
import DataController from "@controllers/DataController";
import { Capacitor } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";
import { AllProgressCheckStatuses, IEvidence, INewDeepLinkRenfrewEvidence, ProgramData } from "@interfaces/Interfaces";
import * as ProgramUtils from "@utils/programUtils";
import * as EvidenceUtils from "@utils/evidenceUtils";
import { useProgramModuleStatus } from "@hooks/programs/useProgramModuleStatus";
import AuditTagErrorModal from "@components/common/AuditTagErrorModal";
import { evidenceFiltersAtom } from "@state/FilterState";
import LearningLoopModal from "@components/common/LearningLoopModal";
import CanadaTimelineEvidenceModal from "@components/common/CanadaTimelineEvidenceModal";
import { menuRoutes, onboardingRoutes, tabRoutes, tabs } from "./routes";
import classNames from "classnames";
import { TRUST_BACKGROUND_COLOUR_VARIANTS } from "src/Constants";
import { isCanada } from "@environment/env";

const Tabs: React.FC = () => {
  const history = useHistory();
  const location = useLocation();

  const popoverRef = useRef<HTMLIonPopoverElement>(null);

  const user = useRecoilValue(userAtom);

  const [reflectionModalVisible, setReflectionModalVisible] = useState<boolean>(false);
  const [learningLoopModalVisible, setLearningLoopModalVisible] = useState<boolean>(false);
  const [canadaTimelineEvidenceModalVisible, setCanadaTimelineEvidenceModalVisible] = useState<boolean>(false);
  const [auditErrorModalVisible, setAuditErrorModalVisible] = useState<boolean>(false);

  const [compactTabBar, setCompactTabBar] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<string>("dashboard");
  const [tabBarVisible, setTabBarVisible] = useState<boolean>(true);
  const [popoverVisible, setPopoverVisible] = useState<boolean>(false);

  const evidence = useRecoilValueLoadable<IEvidence[]>(evidenceAtom);
  const userPrograms = useRecoilValue<ProgramData[] | any[]>(userProgramsAtom);
  const userRef = useRef(user);
  const userProgramsRef = useRef(userPrograms);
  userRef.current = user;
  userProgramsRef.current = userPrograms;
  const allProgressCheckData = useRecoilValueLoadable<AllProgressCheckStatuses[] | null>(progressCheckDataAtom);
  const setEvidenceFilters = useSetRecoilState(evidenceFiltersAtom);

  const { getProgramModulePauseStatus, getProgramModuleFastTrackStatus } = useProgramModuleStatus();

  const [keyboardActive, setKeyboardActive] = useState<boolean>(false);

  useEffect(() => {
    const windowResized = () => {
      if (window.innerWidth < 600) {
        setCompactTabBar(true);
      } else {
        setCompactTabBar(false);
      }
    };

    window.addEventListener("resize", windowResized);

    windowResized();

    return () => {
      window.removeEventListener("resize", windowResized);
    };
  }, []);

  useEffect(() => {
    if (Capacitor.getPlatform() === "android") {
      Keyboard.addListener("keyboardWillShow", () => {
        setKeyboardActive(true);
      });

      Keyboard.addListener("keyboardWillHide", () => {
        setKeyboardActive(false);
      });
    }

    return () => {
      if (Capacitor.getPlatform() === "android") {
        Keyboard.removeAllListeners();
      }
    };
  }, []);

  useEffect(() => {
    const setNavbarColors = async (): Promise<void> => {
      if (!(await DataController.isWebAsync()) && Capacitor.getPlatform() === "android") {
        try {
          NavigationBar.setColor({ color: "#F7FAF8" });
        } catch (error) {
          console.log(error);
        }
      }
    };

    setNavbarColors();
  }, []);

  useEffect(() => {
    const getInitialTab = () => {
      const content = document.getElementsByTagName("ion-tab-bar")[0];
      const initial = content && content.selectedTab;

      if (initial) {
        setSelectedTab(initial);
      }
    };

    getInitialTab();
  }, []);

  const openRenfrewDeeplinkEvidence = (payload: INewDeepLinkRenfrewEvidence): void => {
    const currentUser = userRef.current;
    const program = userProgramsRef.current?.find((item: ProgramData) => ProgramUtils.isRenfrew(item.ID));

    if (!currentUser || !program) {
      return;
    }

    EventRegister.emit(
      "evidence/add-program-evidence",
      EvidenceUtils.buildRenfrewProgramEvidenceDeeplink({
        program,
        user: currentUser,
        deeplink: payload,
      })
    );
  };

  useEffect(() => {
    EventRegister.addEventListener("evidence-modal/add-evidence-guideline", () => setReflectionModalVisible(true));
    EventRegister.addEventListener("evidence-modal/add-evidence-timeline", () => setReflectionModalVisible(true));
    EventRegister.addEventListener("evidence-modal/add-evidence-deeplink", () => setReflectionModalVisible(true));
    EventRegister.addEventListener("evidence-modal/add-learning-loop-deeplink", () => setReflectionModalVisible(true));
    EventRegister.addEventListener("evidence/evidence-card-clicked", () => setReflectionModalVisible(true));
    EventRegister.addEventListener("timeline/edit-evidence-clicked", () => setReflectionModalVisible(true));
    EventRegister.addEventListener("learning-loop/add-evidence-timeline", () => setLearningLoopModalVisible(true));
    EventRegister.addEventListener("learning-loop/add-evidence-deeplink", () => setLearningLoopModalVisible(true));
    EventRegister.addEventListener("canada/add-evidence-timeline", () => setCanadaTimelineEvidenceModalVisible(true));
    EventRegister.addEventListener("evidence/empty-add-clicked", () => setReflectionModalVisible(true));
    EventRegister.addEventListener("evidence/add-program-evidence", () => setReflectionModalVisible(true));
    EventRegister.addEventListener("evidence-modal/add-renfrew-deeplink", openRenfrewDeeplinkEvidence);
    EventRegister.addEventListener("evidence/add-skill-evidence", () => setReflectionModalVisible(true));
    EventRegister.addEventListener("evidence/add-progress-check-evidence", () => setReflectionModalVisible(true));
    EventRegister.addEventListener("tab-bar/visibility-changed", (value) => setTabBarVisible(value));
    EventRegister.addEventListener("evidence/audit-tag-error", () => setAuditErrorModalVisible(true));

    return () => {
      EventRegister.removeEventListener("evidence-modal/add-evidence-guideline");
      EventRegister.removeEventListener("evidence-modal/add-evidence-timeline");
      EventRegister.removeEventListener("evidence-modal/add-evidence-deeplink");
      EventRegister.removeEventListener("evidence-modal/add-learning-loop-deeplink");
      EventRegister.removeEventListener("evidence/evidence-card-clicked");
      EventRegister.removeEventListener("timeline/edit-evidence-clicked");
      EventRegister.removeEventListener("learning-loop/add-evidence-timeline");
      EventRegister.removeEventListener("learning-loop/add-evidence-deeplink");
      EventRegister.removeEventListener("canada/add-evidence-timeline");
      EventRegister.removeEventListener("evidence/empty-add-clicked");
      EventRegister.removeEventListener("evidence/add-program-evidence");
      EventRegister.removeEventListener("evidence-modal/add-renfrew-deeplink");
      EventRegister.removeEventListener("evidence/add-skill-evidence");
      EventRegister.removeEventListener("evidence/add-progress-check-evidence");
      EventRegister.removeEventListener("tab-bar/visibility-changed");
      EventRegister.removeEventListener("evidence/audit-tag-error");
    };
  }, []);

  useEffect(() => {
    EventRegister.addEventListener("profile/view-profile", () => {
      history.replace("/profile");
      setSelectedTab("profile");
    });

    EventRegister.addEventListener("profile/edit-profile", () => {
      history.push("/profile/edit");
    });

    EventRegister.addEventListener("evidence/filter-hcpc", () => {
      history.replace("/evidence");
      setSelectedTab("evidence");
      setEvidenceFilters([{ type: "General", label: "Tagged for audit", option: "Tagged for audit" }]);
    });

    EventRegister.addEventListener("evidence/evidence-deleted", () => {
      try {
        history.location.pathname.includes("/read/") && history.go(-1);
      } catch (error) {
        console.log(error);
      }
    });

    return () => {
      EventRegister.removeEventListener("profile/view-profile");
      EventRegister.removeEventListener("profile/edit-profile");
      EventRegister.removeEventListener("evidence/filter-hcpc");
      EventRegister.removeEventListener("evidence/evidence-deleted");
    };
  }, [history]);

  useEffect(() => {
    const getSelectedTab = (): void => {
      const pathname = location.pathname;

      if (pathname.includes("/dashboard")) {
        setSelectedTab("dashboard");
      } else if (pathname.includes("/timeline")) {
        setSelectedTab("timeline");
      } else if (pathname.includes("/evidence")) {
        setSelectedTab("evidence");
      } else if (pathname.includes("/guidelines")) {
        setSelectedTab("guidelines");
      } else if (pathname.includes("/profile")) {
        setSelectedTab("profile");
      }
    };

    getSelectedTab();
  }, [location]);

  const getIconForTab = (tab: string): string => {
    if (selectedTab === tab) {
      if (tab === "dashboard") {
        return TabBar_Dash_Active;
      }
      if (tab === "guidelines") {
        return Tab_Guidelines_Active;
      }
      if (tab === "evidence") {
        return Tab_Evidence_Active;
      }
      if (tab === "timeline") {
        return Tab_Timeline_Active;
      }
      if (tab === "profile") {
        return Tab_Profile_Active;
      }
    } else {
      if (tab === "dashboard") {
        return TabBar_Dash_Inactive;
      }
      if (tab === "guidelines") {
        return Tab_Guidelines_Inactive;
      }
      if (tab === "evidence") {
        return Tab_Evidence_Inactive;
      }
      if (tab === "timeline") {
        return Tab_Timeline_Inactive;
      }
      if (tab === "profile") {
        return Tab_Profile_Inactive;
      }
    }

    return TabBar_Dash_Active;
  };

  const tabButtonPressed = (tab: string): void => {
    if (selectedTab === tab) {
      try {
        const element = document.getElementById(`${tab}ContentContainer`);

        element?.scrollIntoView({ behavior: "smooth" });
      } catch (error) {
        console.log(error);
      }
    }
  };

  const tabIsActive = (tab: string): boolean => {
    return selectedTab === tab;
  };

  const tabChanged = (event: any): void => {
    setSelectedTab(event.detail.tab);
    FirebaseService.logEvent("tab_pressed", { tab: event.detail.tab });
  };

  const addEvidencePressed = async (): Promise<void> => {
    await FirebaseService.logEvent("add_evidence_pressed", {
      tab: selectedTab,
      type: "add_evidence_fab",
    });
  };

  const addProgramEvidencePressed = async (program: ProgramData): Promise<void> => {
    await FirebaseService.logEvent("add_program_evidence_pressed", {
      tab: selectedTab,
      program: program.Title,
    });
  };

  const toggleModalVisible = (value: boolean): void => {
    setReflectionModalVisible(value);
  };

  const evidenceButtonPressed = () => {
    setPopoverVisible(false);
    toggleModalVisible(true);
  };

  const evidenceFABPressed = async (): Promise<void> => {
    if (userPrograms.length > 0) {
      setPopoverVisible(true);
    } else {
      addEvidencePressed();
      toggleModalVisible(true);
    }
  };

  const addProgramEvidence = (program: ProgramData): void => {
    addProgramEvidencePressed(program);

    let data: object = {};
    const competences = program.CompetenceActivity ?? [];
    const progressChecks = program.ProgressChecks ?? [];
    const evidenceDefinitions = program["Evidence Definitions"] ?? [];
    const mandatoryStandards = ProgramUtils.getMandatoryStandards(user, program);
    const optionalStandards = ProgramUtils.getOptionalStandards(user, program);
    const skills = ProgramUtils.getSkillsForRole(user, program);
    const usesAdditionalEvidenceDefinitions = program.UsesAdditionalEvidenceDefinitions;
    const addProgramEvidenceType = ProgramUtils.getAddProgramEvidenceType(program.ID);

    if (addProgramEvidenceType.type === "skill") {
      data = {
        program,
        ...(competences.length > 0 && { competences }),
        ...(evidenceDefinitions.length > 0 &&
          usesAdditionalEvidenceDefinitions && {
            evidenceDefinitions: evidenceDefinitions[0],
          }),
        ...(mandatoryStandards.length > 0 && { mandatoryStandards }),
        ...(optionalStandards.length > 0 && { optionalStandards }),
        ...(skills.length > 0 && { skills }),
        skill: null,
      };
    } else {
      data = {
        program,
        ...(competences.length > 0 && { competences }),
        ...(evidenceDefinitions.length > 0 &&
          !usesAdditionalEvidenceDefinitions && {
            evidenceDefinitions: evidenceDefinitions[0],
          }),
        ...(progressChecks.length > 0 && { progressChecks }),
        ...(mandatoryStandards.length > 0 && { mandatoryStandards }),
        ...(optionalStandards.length > 0 && { optionalStandards }),
        ...(skills.length > 0 && { skills }),
        skill: null,
      };
    }

    closeFABPopover();
    EventRegister.emit("evidence/add-program-evidence", data);
  };

  const closeFABPopover = (): void => {
    setPopoverVisible(false);
  };

  return (
    <IonContent
      id="contentMain"
      className="tabsContainer"
    >
      <div
        id="deep-linking-active"
        className="hidden"
      />
      <LearningLoopModal
        visible={learningLoopModalVisible}
        closeModal={() => setLearningLoopModalVisible(false)}
      />
      {isCanada && (
        <CanadaTimelineEvidenceModal
          visible={canadaTimelineEvidenceModalVisible}
          closeModal={() => setCanadaTimelineEvidenceModalVisible(false)}
        />
      )}
      <EvidenceModal
        visible={reflectionModalVisible}
        closeModal={(value) => toggleModalVisible(value)}
      />
      <AuditTagErrorModal
        visible={auditErrorModalVisible}
        dismissPressed={() => setAuditErrorModalVisible(false)}
      />
      <IonTabs>
        <IonRouterOutlet
          id="main"
          mode="ios"
        >
          {tabRoutes.map((route) => (
            <Route
              exact
              key={route.path}
              path={route.path}
              component={route.component}
            />
          ))}
          {menuRoutes.map((route) => (
            <Route
              exact
              key={route.path}
              path={route.path}
              component={route.component}
            />
          ))}
          {onboardingRoutes.map((route) => (
            <Route
              exact
              key={route.path}
              path={route.path}
              render={() => <Redirect to={tabs[0].href} />}
            />
          ))}
          <Route
            exact
            path="/"
            render={() => <Redirect to={tabs[0].href} />}
          />
        </IonRouterOutlet>
        <IonTabBar
          slot="bottom"
          className={`portfolioTabBar ${tabBarVisible ? "" : "tabBarHidden"}`}
          onIonTabsDidChange={(event) => tabChanged(event)}
          selectedTab={selectedTab}
        >
          <IonTabButton
            tab="dashboard"
            href="/dashboard"
            mode="ios"
            onClick={() => tabButtonPressed("dashboard")}
            layout={compactTabBar ? "icon-top" : "icon-start"}
          >
            <img
              src={getIconForTab("dashboard")}
              className={compactTabBar ? "tabBarIconTop" : "tabBarIconStart"}
            />
            <IonLabel
              className={`${compactTabBar ? "tabBarLabelTop" : "tabBarLabelStart"} ${tabIsActive("dashboard") ? "tabBarLabelActive" : ""}`}
            >
              {"Dashboard"}
            </IonLabel>
          </IonTabButton>
          <IonTabButton
            tab="timeline"
            href="/timeline"
            mode="ios"
            onClick={() => tabButtonPressed("timeline")}
            layout={compactTabBar ? "icon-top" : "icon-start"}
          >
            <img
              src={getIconForTab("timeline")}
              className={compactTabBar ? "tabBarIconTop" : "tabBarIconStart"}
            />
            <IonLabel
              className={`${compactTabBar ? "tabBarLabelTop" : "tabBarLabelStart"} ${tabIsActive("timeline") ? "tabBarLabelActive" : ""}`}
            >
              {"Timeline"}
            </IonLabel>
          </IonTabButton>
          <IonTabButton
            tab="evidence"
            href="/evidence"
            mode="ios"
            onClick={() => tabButtonPressed("evidence")}
            layout={compactTabBar ? "icon-top" : "icon-start"}
          >
            <img
              src={getIconForTab("evidence")}
              className={compactTabBar ? "tabBarIconTop" : "tabBarIconStart"}
            />
            <IonLabel
              className={`${compactTabBar ? "tabBarLabelTop" : "tabBarLabelStart"} ${tabIsActive("evidence") ? "tabBarLabelActive" : ""}`}
            >
              {"Evidence"}
            </IonLabel>
          </IonTabButton>
          <IonTabButton
            tab="profile"
            href="/profile"
            mode="ios"
            onClick={() => tabButtonPressed("profile")}
            layout={compactTabBar ? "icon-top" : "icon-start"}
          >
            <img
              src={getIconForTab("profile")}
              className={compactTabBar ? "tabBarIconTop" : "tabBarIconStart"}
            />
            <IonLabel
              className={`${compactTabBar ? "tabBarLabelTop" : "tabBarLabelStart"} ${tabIsActive("profile") ? "tabBarLabelActive" : ""}`}
            >
              {"Profile"}
            </IonLabel>
          </IonTabButton>
        </IonTabBar>
      </IonTabs>
      {!keyboardActive && !location.pathname.includes("program") && (
        <div
          className="fabContainer"
          style={{ maxWidth: DataController.isWeb() ? 980 : undefined }}
        >
          <IonFab
            className={`tabBarFab ${tabBarVisible ? "" : "tabBarHidden"}`}
            vertical="bottom"
            id="evidenceFab"
            horizontal="end"
            slot="fixed"
          >
            <IonFabButton
              onClick={() => evidenceFABPressed()}
              activated={popoverVisible}
            >
              <IonIcon
                icon={add}
                title="Add Evidence"
              ></IonIcon>
            </IonFabButton>
          </IonFab>
          {userPrograms.length > 0 && (
            <IonPopover
              id="fabPopover"
              className="fabPopover"
              ref={popoverRef}
              trigger="evidenceFab"
              mode="ios"
              isOpen={popoverVisible}
              onDidDismiss={() => setPopoverVisible(false)}
              animated={true}
              reference="trigger"
              side="top"
              keepContentsMounted={true}
              alignment="end"
              backdropDismiss={true}
            >
              <div
                className="!max-w-[400px]"
                style={{ overflowY: "auto", maxHeight: 408 }}
              >
                <div className="flex flex-col gap-2 p-3">
                  <div className="overflow-hidden rounded-xl bg-white shadow-sm">
                    {userPrograms.map((program: ProgramData, index: number) => {
                      const isDisabled = ProgramUtils.disableFABProgramButton(
                        program,
                        allProgressCheckData.contents,
                        evidence.state === "hasValue" ? evidence.contents : [],
                        user,
                        getProgramModuleFastTrackStatus(program.ID) ?? false,
                        getProgramModulePauseStatus(program.ID) ?? false
                      );
                      return (
                        <React.Fragment key={program.ID}>
                          {index > 0 && <div className="h-px bg-grey-30" />}
                          <button
                            disabled={isDisabled}
                            className={classNames(
                              "flex w-full flex-row items-center justify-between gap-3 px-3 py-[11px] text-left",
                              isDisabled ? "opacity-40" : "active:opacity-70",
                              "hover:bg-grey-20"
                            )}
                            onClick={() => addProgramEvidence(program)}
                          >
                            <div
                              className={classNames(
                                "flex shrink-0 items-center justify-center rounded px-1 py-0.5 text-13px font-semibold text-white",
                                `${TRUST_BACKGROUND_COLOUR_VARIANTS[program.TrustName!] ?? "!bg-NEAS"}`
                              )}
                            >
                              {program.TrustName ?? "XXX"}
                            </div>
                            <div className="flex flex-1 items-end justify-end">
                              <div className="fabPopoverIonLabel min-w-0 flex-1 text-balance">{`Add ${program.EvidenceButtonText ?? program.Title}`}</div>
                            </div>
                          </button>
                        </React.Fragment>
                      );
                    })}
                  </div>
                  <button
                    className="inline-flex items-center self-end rounded-xl bg-white px-3 py-[11px] shadow-sm active:opacity-70"
                    onClick={() => evidenceButtonPressed()}
                  >
                    <span className="fabPopoverIonLabel">{"Add Personal evidence"}</span>
                  </button>
                </div>
              </div>
            </IonPopover>
          )}
        </div>
      )}
    </IonContent>
  );
};

export default Tabs;
